import { useState } from 'react';
import { LOGO_CMOB } from '../../data/constants';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || '';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || '';

// ─── ÉCRAN DE CONNEXION ──────────────────────────────────────────────────────
// Utilise Supabase Auth natif.
// • Le brute force est géré côté serveur Supabase (blocage automatique).
// • Le reset de mot de passe est géré par Supabase (email avec lien sécurisé).
// • Plus de PBKDF2 côté client — Supabase gère bcrypt côté serveur.
//
// Props :
//   onLogin(user)   — callback après connexion réussie (user = profil normalisé)
//   onLogEvent(evt) — callback audit log
//   signIn(email, password) → Promise<string|null> — depuis useAuth()
//   paramsAPI, infosAsso — pour les emails de reset (optionnel)

// ── Parse le fragment URL (#access_token=...&type=recovery) ─────────────────
function parseHashParams() {
  const hash = window.location.hash.replace(/^#/, '');
  const params = {};
  hash.split('&').forEach(part => {
    const [k, v] = part.split('=');
    if (k) params[decodeURIComponent(k)] = decodeURIComponent(v || '');
  });
  return params;
}

function Login({ onLogin, onLogEvent, signIn, paramsAPI, infosAsso }) {
  const [identifier, setIdentifier] = useState('');
  const [password,   setPassword]   = useState('');
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Détection du token dans l'URL (lien email reset / invitation) ─────────
  const hashParams  = parseHashParams();
  const hashToken   = hashParams['access_token'] || '';
  const hashType    = hashParams['type'] || ''; // 'recovery' | 'invite' | 'signup'
  const needsSetPwd = !!hashToken && ['recovery', 'invite', 'signup'].includes(hashType);

  // 'login' | 'reset_ask' | 'reset_sent' | 'set_password'
  const [mode,         setMode]         = useState(needsSetPwd ? 'set_password' : 'login');
  const [resetEmail,   setResetEmail]   = useState('');
  const [resetMsg,     setResetMsg]     = useState('');
  const [resetError,   setResetError]   = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // ── Formulaire "définir nouveau mot de passe" (depuis lien email) ─────────
  const [newPwd,       setNewPwd]       = useState('');
  const [confirmPwd,   setConfirmPwd]   = useState('');
  const [setPwdError,  setSetPwdError]  = useState('');
  const [setPwdOk,     setSetPwdOk]     = useState(false);
  const [setPwdLoading,setSetPwdLoading]= useState(false);
  const [visible,      setVisible]      = useState(false);

  const regles = [
    { ok: newPwd.length >= 8,                      label: '8 caractères minimum' },
    { ok: /[A-Z]/.test(newPwd),                    label: '1 majuscule' },
    { ok: /[0-9]/.test(newPwd),                    label: '1 chiffre' },
    { ok: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPwd), label: '1 caractère spécial' },
  ];
  const robustesse   = regles.filter(r => r.ok).length;
  const mdpValide    = robustesse === 4;
  const couleurBarre = robustesse <= 1 ? '#dc2626' : robustesse === 2 ? '#f59e0b' : robustesse === 3 ? '#3b82f6' : '#22c55e';

  const handleSetPassword = async () => {
    setSetPwdError('');
    if (!mdpValide)          { setSetPwdError('Le mot de passe ne respecte pas les critères.'); return; }
    if (newPwd !== confirmPwd) { setSetPwdError('Les mots de passe ne correspondent pas.'); return; }
    setSetPwdLoading(true);
    try {
      // Utiliser le access_token du lien pour mettre à jour le mot de passe
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          apikey:          SUPABASE_ANON,
          Authorization:   `Bearer ${hashToken}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({ password: newPwd }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || err.error_description || `Erreur HTTP ${r.status}`);
      }
      // Nettoyer le hash de l'URL pour éviter la réutilisation du token
      window.history.replaceState(null, '', window.location.pathname);
      setSetPwdOk(true);
    } catch (e) {
      setSetPwdError('Erreur : ' + e.message);
    }
    setSetPwdLoading(false);
  };

  // ── Connexion via Supabase Auth ───────────────────────────────────────────
  const handleSubmit = async () => {
    if (loading) return;
    if (!identifier.trim() || !password.trim()) {
      setError('Veuillez saisir votre identifiant et votre mot de passe.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const raw = identifier.trim().toLowerCase();
      const supaEmail = raw.includes('@') ? raw : `${raw}@cmob.internal`;
      const errMsg = await signIn(supaEmail, password);
      if (errMsg) {
        setError(errMsg);
        if (onLogEvent) onLogEvent({
          type: 'connexion', statut: 'échec',
          login: raw, at: new Date().toISOString(),
        });
      }
    } catch (e) {
      setError('Erreur de connexion. Réessayez.');
    }
    setLoading(false);
  };

  // ── Réinitialisation mot de passe via Supabase ────────────────────────────
  const envoyerResetEmail = async () => {
    setResetError(''); setResetMsg('');
    if (!resetEmail.trim()) { setResetError('Saisissez votre adresse email.'); return; }
    setResetLoading(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email:      resetEmail.trim().toLowerCase(),
          redirectTo: window.location.origin,
        }),
      });
      if (r.ok || r.status === 200) {
        setMode('reset_sent');
        setResetMsg(`Un email de réinitialisation a été envoyé à ${resetEmail.trim()} si ce compte existe.`);
      } else {
        throw new Error(`HTTP ${r.status}`);
      }
    } catch (e) {
      setResetError('Erreur lors de l\'envoi. Réessayez ou contactez votre administrateur.');
    }
    setResetLoading(false);
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <img src={LOGO_CMOB} alt="Logo CMOB"
            style={{ maxWidth: 200, marginBottom: 8, display: 'block', margin: '0 auto 8px' }}/>
        </div>

        {/* ── DÉFINIR NOUVEAU MOT DE PASSE (depuis lien email) ── */}
        {mode === 'set_password' && <>
          <div className="login-title" style={{ fontSize: '1rem' }}>
            {hashType === 'recovery' ? '🔐 Réinitialisation du mot de passe' : '🔐 Définir votre mot de passe'}
          </div>

          {setPwdOk ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>✅</div>
              <div style={{ fontWeight: 700, color: 'var(--success)', marginBottom: 12 }}>
                Mot de passe défini avec succès !
              </div>
              <button className="login-btn" onClick={() => setMode('login')}>
                → Se connecter
              </button>
            </div>
          ) : (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 16, textAlign: 'center' }}>
                Choisissez un nouveau mot de passe sécurisé.
              </p>
              {setPwdError && <div className="login-error">{setPwdError}</div>}

              <div className="login-field">
                <label>Nouveau mot de passe *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={visible ? 'text' : 'password'}
                    value={newPwd}
                    onChange={e => setNewPwd(e.target.value)}
                    placeholder="Nouveau mot de passe"
                    style={{ paddingRight: 40 }}
                  />
                  <button onClick={() => setVisible(v => !v)} tabIndex={-1}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>
                    {visible ? '🙈' : '👁️'}
                  </button>
                </div>
                {newPwd && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ flex: 1, height: 4, borderRadius: 2,
                          background: i <= robustesse ? couleurBarre : '#e5e7eb',
                          transition: 'background 0.2s' }}/>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {regles.map(({ ok, label }) => (
                        <span key={label} style={{ fontSize: '0.7rem', color: ok ? '#22c55e' : '#9ca3af' }}>
                          {ok ? '✓' : '○'} {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="login-field">
                <label>Confirmer le mot de passe *</label>
                <input
                  type={visible ? 'text' : 'password'}
                  value={confirmPwd}
                  onChange={e => setConfirmPwd(e.target.value)}
                  placeholder="Répéter le mot de passe"
                  onKeyDown={e => e.key === 'Enter' && handleSetPassword()}
                  style={{ border: confirmPwd && newPwd !== confirmPwd ? '1.5px solid #dc2626' : undefined }}
                />
                {confirmPwd && newPwd !== confirmPwd && (
                  <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 4 }}>
                    Les mots de passe ne correspondent pas
                  </div>
                )}
              </div>

              <button className="login-btn"
                onClick={handleSetPassword}
                disabled={setPwdLoading || !mdpValide || newPwd !== confirmPwd}>
                {setPwdLoading ? '⏳ Enregistrement…' : '🔐 Définir le mot de passe'}
              </button>
            </>
          )}
        </>}

        {/* ── CONNEXION ── */}
        {mode === 'login' && <>
          <div className="login-title">Connexion Intranet</div>
          {error && <div className="login-error">{error}</div>}

          <div className="login-field">
            <label>Identifiant ou Email</label>
            <input
              type="text"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="login ou votre.email@cmobassens.fr"
              autoComplete="username"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          <div className="login-field">
            <label>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          <button className="login-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Vérification…' : 'Se connecter →'}
          </button>

          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button
              onClick={() => { setMode('reset_ask'); setResetError(''); setResetMsg(''); setResetEmail(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--bleu)', fontSize: '0.8rem', textDecoration: 'underline', fontFamily: 'inherit' }}>
              Mot de passe oublié ?
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'center' }}>
            Accès réservé au personnel autorisé CMOB
          </div>
        </>}

        {/* ── RESET : saisir l'email ── */}
        {mode === 'reset_ask' && <>
          <div className="login-title" style={{ fontSize: '1rem' }}>🔑 Mot de passe oublié</div>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 16, textAlign: 'center' }}>
            Saisissez votre adresse email — vous recevrez un lien de réinitialisation.
          </p>
          {resetError && <div className="login-error">{resetError}</div>}

          <div className="login-field">
            <label>Adresse email</label>
            <input
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="votre.email@cmobassens.fr"
              onKeyDown={e => e.key === 'Enter' && envoyerResetEmail()}
            />
          </div>

          <button className="login-btn" onClick={envoyerResetEmail}
            disabled={resetLoading || !resetEmail.trim()}>
            {resetLoading ? '⏳ Envoi…' : '📧 Envoyer le lien'}
          </button>

          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button onClick={() => setMode('login')}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--muted)', fontSize: '0.8rem', fontFamily: 'inherit' }}>
              ← Retour à la connexion
            </button>
          </div>
        </>}

        {/* ── RESET : email envoyé ── */}
        {mode === 'reset_sent' && <>
          <div className="login-title" style={{ fontSize: '1rem' }}>📬 Email envoyé</div>
          <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8,
            color: 'var(--success)', fontSize: '0.82rem', marginBottom: 16,
            fontWeight: 600, textAlign: 'center' }}>
            {resetMsg}
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', textAlign: 'center', marginBottom: 16 }}>
            Cliquez sur le lien dans l'email pour définir votre nouveau mot de passe.
            Le lien est valable 1 heure.
          </p>
          <button className="login-btn" onClick={() => setMode('login')}>
            ← Retour à la connexion
          </button>
        </>}
      </div>
    </div>
  );
}

export { Login };
